/**
 * Known browser / email-scanner / extension noise. These aren't bugs in
 * our code — they're events other software throws into the page after we
 * load, then bubbles up as window.onerror or unhandledrejection.
 *
 * Two lists:
 *
 *   WINDOW_NOISE_PATTERNS — only dropped when `componentName === 'window'`.
 *   An app-code catch that genuinely sees one of these strings ("Failed to
 *   fetch", "removeChild on null") is a real bug we want to see, so we
 *   don't filter those. Only unhandled/window bubbles are noise.
 *
 *   ALWAYS_NOISE_PATTERNS — dropped regardless of componentName. These
 *   are never a real app bug even when explicitly logged: browser-extension
 *   noise (MetaMask), perf data logged from a stale bundle (long tasks),
 *   etc. If the code path that emits these is under our control we should
 *   fix it at the source; the filter is a backstop for cached old JS.
 */

const WINDOW_NOISE_PATTERNS: RegExp[] = [
  // Microsoft Outlook Safe Links / link preview scanner. Outlook opens
  // the URL in a hidden browser to check for phishing; its injected
  // script posts a message to a parent that doesn't exist, throwing
  // this exact rejection into whatever page it landed on. 3.3k hits
  // from 6 sessions in the first 15h post-deploy.
  /Object Not Found Matching Id:\d+, MethodName:(update|apply), ParamCount:\d+/,

  // ResizeObserver benign warnings — Chrome fires these when a layout
  // pass hasn't finished; nothing to fix.
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/,

  // Google Translate / other content scripts touching detached DOM.
  /Cannot read properties of null \(reading 'removeChild'\)/,

  // Cross-origin script error. Browsers strip the details for security,
  // leaving only this string — no stack, no line. Nothing we can act on.
  /^Script error\.?$/,

  // Clipboard permission errors — the browser said "no" to
  // navigator.clipboard.readText(). User denied the prompt or the tab
  // isn't focused; not an app bug.
  /Clipboard (read|write) operation is not allowed/,
  /Failed to execute '(readText|writeText|read|write)' on 'Clipboard'/,

  // Generic permission-denied surface: notification/getUserMedia/clipboard
  // prompts the user said no to. The message text is identical across
  // APIs; treat all as user choices, not bugs.
  /^The request is not allowed by the user agent or the platform in the current context/,

  // Android WebView bridge — postMessage into a Java host that already
  // GC'd its handler. Fires when a student navigates away mid-request.
  /Error invoking postMessage: Java (exception was raised|object is gone)/,

  // Safari-specific generic network failure (aborted request / offline
  // / navigated away mid-fetch). Same class as Chrome's "Failed to
  // fetch" — not our bug.
  /^Load failed$/,

  // Next.js Server Action E394 — thrown by the app-router runtime when
  // an action's response isn't the expected RSC/redirect shape. Fires
  // when the user navigates away mid-action, when middleware intercepts
  // an action, or on transient CDN hiccups. All 76 recent hits carry
  // the same tiny helper stack (`F@…chunks/…js:2:743`), Chrome only, on
  // unauthed marketing pages — no app code to fix.
  /^An unexpected response was received from the server\.$/,

  // Browser extension / injected-script globals leaking into our window.
  // These are third-party bookmarklets, LinkedIn/Meta scanners, or old
  // Prestashop-flavored extensions that assume every page has jQuery /
  // $ / googletag / prestashop globals defined. When they don't, the
  // extension's inline script throws — but the throw bubbles into our
  // window.onerror. Nothing we can fix from our side.
  /^(jQuery|\$|googletag|prestashop) is not defined$/,

  // Chrome's own throttle warning when a script hits the history-state
  // rate limit. Emitted by the browser itself, not our code.
  /Attempt to use history\.replaceState\(\) more than \d+ times per \d+ seconds/,

  // Cross-origin syntax errors — browser extensions injecting invalid
  // JS into pages, or third-party <script> tags that error out during
  // parse. Nothing actionable without source.
  /^Invalid or unexpected token$/,

  // Browser extension globals like NHTFlag (Chrome accessibility tools
  // / vendor-specific injected properties). Same class as the "not
  // defined" globals — extension noise.
  /Cannot read properties of undefined \(reading 'NHTFlag'\)/,
]

const ALWAYS_NOISE_PATTERNS: RegExp[] = [
  // Legacy PerformanceObserver longtask logger (removed in commit
  // 9677004f). Still fires from cached bundles — componentName is
  // 'PerformanceMonitor', not 'window', so the window gate misses it.
  // Long tasks are perf data, not errors; drop everywhere.
  /^Long task detected \(\d+ms\)$/,

  // MetaMask/browser-extension probes that dispatch synthetic errors
  // when their inpage bridge can't reach the extension. Never our bug.
  /Failed to connect to MetaMask/,

  // ChunkLoadError from a deploy race — the user's browser is trying to
  // fetch a bundle from the previous release that Vercel no longer
  // serves. Our top-level ErrorBoundary handles recovery (reload); the
  // event isn't actionable and floods the viewer every deploy.
  /^Failed to load chunk\b/,
  /^Loading chunk \S+ failed\b/,
]

export function isKnownBrowserNoise(message: string): boolean {
  if (!message) return false
  return WINDOW_NOISE_PATTERNS.some((p) => p.test(message))
}

export function isAlwaysNoise(message: string): boolean {
  if (!message) return false
  return ALWAYS_NOISE_PATTERNS.some((p) => p.test(message))
}
